import { getSupabase } from '@/lib/supabase';
import { currentReason, isReasonRequired, obtainReason } from '@/lib/changeReason';

export { describeError } from '@/lib/describeError';
import { describeError } from '@/lib/describeError';

/**
 * Generic access to the `api` views.
 *
 * Every view is `security_invoker` and the writable ones carry an INSTEAD OF trigger that
 * writes as the caller (migration 0022), so nothing here filters by organisation, geography or
 * record scope - the database has already done it, and a second copy of those rules in the
 * browser would only be a weaker one that drifts.
 */

export interface ListOptions {
  /** column -> value. Null and undefined are dropped, so optional filters need no branching. */
  filters?: Record<string, string | number | boolean | null | undefined>;
  search?: { columns: string[]; term: string };
  orderBy?: { column: string; ascending?: boolean };
  limit?: number;
  /** Soft-deleted rows are hidden unless asked for. */
  includeDeleted?: boolean;
}

export async function listRows<T>(view: string, options: ListOptions = {}): Promise<T[]> {
  let query = getSupabase().from(view as never).select('*');

  for (const [column, value] of Object.entries(options.filters ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    query = query.eq(column, value as never);
  }

  const term = options.search?.term?.trim();
  if (term && options.search) {
    // `or` takes a comma-separated filter list; a comma or parenthesis inside the term would
    // be read as syntax, so they are stripped rather than escaped.
    const safe = term.replace(/[,()*]/g, ' ').trim();
    if (safe) {
      query = query.or(options.search.columns.map((column) => `${column}.ilike.%${safe}%`).join(','));
    }
  }

  if (options.orderBy) {
    query = query.order(options.orderBy.column, { ascending: options.orderBy.ascending ?? true });
  }
  query = query.limit(options.limit ?? 200);

  const { data, error } = await query;
  if (error) throw new Error(describeError(error));
  return (data ?? []) as T[];
}

export async function getRow<T>(view: string, id: string): Promise<T | null> {
  const { data, error } = await getSupabase().from(view as never).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(describeError(error));
  return (data ?? null) as T | null;
}

type Result = { data: unknown; error: { message?: string; code?: string } | null };

/**
 * Run a write; if the database asks for a reason (an app admin working in another organisation),
 * ask for it once and retry with it attached as the x-change-reason header.
 */
/**
 * Rows whose `column` is any of `values`, in batches small enough that no batch reaches the
 * 200-row cap `listRows` applies - a truncated answer read as whole is how a bulk change misses
 * records. Batches of 25 suit views with a handful of rows per value (a plot's scopes).
 */
export async function listRowsIn<T>(view: string, column: string, values: string[], perBatch = 25): Promise<T[]> {
  const out: T[] = [];
  for (let start = 0; start < values.length; start += perBatch) {
    const { data, error } = await getSupabase().from(view as never).select('*')
      .in(column, values.slice(start, start + perBatch) as never).limit(1000);
    if (error) throw new Error(describeError(error));
    out.push(...((data ?? []) as T[]));
  }
  return out;
}

async function write(run: (reason: string | null) => PromiseLike<Result>): Promise<unknown> {
  let result = await run(currentReason());
  if (result.error && isReasonRequired(result.error.message)) {
    const reason = await obtainReason(result.error.message ?? '');
    if (!reason) throw new Error('Not saved: a reason is needed for changes inside another organisation.');
    result = await run(reason);
  }
  if (result.error) throw new Error(describeError(result.error));
  return result.data;
}

function withReason<B extends { setHeader: (name: string, value: string) => B }>(builder: B, reason: string | null): B {
  return reason ? builder.setHeader('x-change-reason', encodeURIComponent(reason)) : builder;
}

export async function createRow<T>(view: string, values: Record<string, unknown>): Promise<T> {
  return (await write((reason) => withReason(getSupabase().from(view as never).insert(values as never).select().single(), reason))) as T;
}

export async function saveRow<T>(view: string, id: string, values: Record<string, unknown>): Promise<T> {
  return (await write((reason) => withReason(getSupabase().from(view as never).update(values as never).eq('id', id).select().single(), reason))) as T;
}

/**
 * Business records are never hard deleted: the database blocks DELETE outright and demands a
 * reason on the soft delete, so "remove" is an update and the reason is not optional.
 */
export async function removeRow(view: string, id: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new Error('A reason is required to remove a record.');
  await write((changeReason) => withReason(getSupabase()
    .from(view as never)
    .update({ is_deleted: true, delete_reason: reason.trim() } as never)
    .eq('id', id), changeReason ?? reason.trim()));
}

export async function callRpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  return (await write((reason) => withReason(getSupabase().rpc(name as never, args as never), reason))) as T;
}
